"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { MessageSquarePlus, ArrowRight, Tag, Gift, ChevronDown, ChevronUp, Clock, Zap } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import PromotionDetail from "@/app/dashboard/services-request/components/promotion-detail";
import { startSalesFeedback } from "@/app/dashboard/services-request/lib/actions";

const PROMO_CLOSE_TTL_HOURS = 24;
const PROMO_STORAGE_KEY = "promotions_panel_closed_until";

interface Promo {
  id: string;
  title: string;
  description: string;
  tag: string;
  duration: Date | string;
  image: string;
}

interface Props {
  services: { id: string; title: string; description?: string | null; image?: string | null }[];
  promotions?: Promo[];
}

const cloudName = process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME;
function cloudUrl(id: string) {
  return id.startsWith("http")
    ? id
    : `https://res.cloudinary.com/${cloudName}/image/upload/${id}`;
}

function PromoCard({ promo, onViewDetails }: { promo: Promo; onViewDetails: () => void }) {
  const isNew = promo.tag === "New";
  return (
    <button
      onClick={onViewDetails}
      className="w-full rounded-[14px] border border-border overflow-hidden flex items-stretch hover:border-primary/40 hover:shadow-sm transition-all text-left group"
    >
      {promo.image && (
        <div className="w-[72px] flex-shrink-0 relative">
          <Image src={cloudUrl(promo.image)} alt={promo.title} fill sizes="72px" className="object-cover" />
        </div>
      )}
      <div className="p-3 flex flex-col justify-center min-w-0 flex-1">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-primary mb-1">
          {isNew ? <Tag size={9} /> : <Clock size={9} />}
          {promo.tag}
        </span>
        <p className="text-sm font-semibold truncate">{promo.title}</p>
        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{promo.description}</p>
      </div>
      <div className="flex items-center pr-3 flex-shrink-0">
        <ArrowRight size={13} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </button>
  );
}

function PromotionsAccordion({ promotions }: { promotions: Promo[] }) {
  // Closed by default — user should focus on services first
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState<Promo | null>(null);
  const router = useRouter();

  useEffect(() => {
    setMounted(true);
    // Only auto-open if they previously had it open (and TTL hasn't expired)
    const until = localStorage.getItem(PROMO_STORAGE_KEY);
    if (!until || Date.now() > Number(until)) {
      // default closed — don't auto-open
    }
  }, []);

  const handleToggle = () => {
    if (open) {
      localStorage.setItem(
        PROMO_STORAGE_KEY,
        String(Date.now() + PROMO_CLOSE_TTL_HOURS * 60 * 60 * 1000),
      );
    }
    setOpen((v) => !v);
  };

  const handleStartConversation = async (title: string) => {
    const res = await startSalesFeedback(
      title,
      `Hi, I'm interested in the "${title}" promotion.`,
    );
    if (res?.feedbackId) {
      router.push(`/dashboard/messages/${res.feedbackId}`);
    }
    setSelectedPromo(null);
  };

  return (
    <>
      <section className="rounded-[16px] border border-border overflow-hidden bg-background">
        <button
          onClick={handleToggle}
          className="flex items-center justify-between w-full px-4 py-3.5 hover:bg-accent/40 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <div className="relative flex-shrink-0">
              <Gift size={15} className="text-primary" />
              {mounted && !open && promotions.length > 0 && (
                <span className="absolute -top-1 -right-1 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">Current Offers</span>
              {promotions.length > 0 && (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full transition-colors ${
                  !open ? "bg-primary text-white" : "bg-primary/10 text-primary"
                }`}>
                  {promotions.length} active
                </span>
              )}
            </div>
          </div>
          <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${open ? "bg-accent" : ""}`}>
            {open ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}
          </div>
        </button>

        <AnimatePresence initial={false}>
          {mounted && open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="flex flex-col gap-2.5 px-4 pb-5 pt-3 border-t border-border">
                {promotions.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-4 text-center">No active promotions right now.</p>
                ) : (
                  promotions.map((promo) => (
                    <PromoCard
                      key={promo.id}
                      promo={promo}
                      onViewDetails={() => setSelectedPromo(promo)}
                    />
                  ))
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      <PromotionDetail
        promo={selectedPromo}
        open={!!selectedPromo}
        onClose={() => setSelectedPromo(null)}
        onStartConversation={handleStartConversation}
      />
    </>
  );
}

export default function MessagesEmptyState({ services, promotions = [] }: Props) {
  const router = useRouter();

  const handleServiceClick = (service: { id: string; title: string }) => {
    const params = new URLSearchParams({
      serviceId: service.id,
      serviceLabel: service.title,
    });
    router.push(`/dashboard/messages/new?${params.toString()}`);
  };

  return (
    <div className="flex-1 overflow-y-auto px-5 py-6 space-y-5 ">
      {/* Hero CTA */}
      <div className="rounded-[20px] bg-primary p-6 text-white">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest opacity-70 mb-1">
              Start here
            </p>
            <h2 className="text-[17px] font-bold leading-snug mb-2">
              Let&apos;s build something great together
            </h2>
            <p className="text-sm opacity-80 max-w-[280px]">
              Tell us what you need. We&apos;ll scope it, price it, and get moving fast.
            </p>
          </div>
          <div className="hidden sm:flex w-12 h-12 rounded-full bg-white/20 items-center justify-center flex-shrink-0">
            <MessageSquarePlus size={22} className="text-white" />
          </div>
        </div>
        <button
          onClick={() => router.push("/dashboard/messages/new")}
          className="mt-5 inline-flex items-center gap-2 bg-white text-primary text-sm font-semibold px-4 py-2 rounded-full hover:opacity-90 transition-opacity"
        >
          Start a conversation
          <ArrowRight size={14} />
        </button>
      </div>

      {/* Services grid */}
      {services.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              Our Services
            </p>
            <Link href="/services" className="text-xs text-primary hover:underline">
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {services.map((service) => (
              <button
                key={service.id}
                onClick={() => handleServiceClick(service)}
                className="flex flex-col rounded-[14px] border border-border hover:border-primary/40 hover:shadow-sm overflow-hidden transition-all text-left group bg-background"
              >
                {/* Image area */}
                <div className="w-full aspect-[4/3] bg-muted/5 flex-shrink-0 overflow-hidden relative">
                  {service.image ? (
                    <Image
                      src={cloudUrl(service.image)}
                      alt={service.title}
                      fill
                      sizes="(min-width: 640px) 33vw, 50vw"
                      className="object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Zap size={22} className="text-muted-foreground/40" />
                    </div>
                  )}
                </div>
                {/* Label + description */}
                <div className="px-3 py-2.5 flex flex-col gap-0.5">
                  <div className="flex items-start justify-between gap-1">
                    <p className="text-[13px] font-semibold leading-tight line-clamp-1">{service.title}</p>
                    <ArrowRight
                      size={12}
                      className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5"
                    />
                  </div>
                  {service.description && (
                    <p className="text-[11px] text-muted-foreground leading-snug line-clamp-2">
                      {service.description}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Promotions accordion — closed by default */}
      {promotions.length > 0 && <PromotionsAccordion promotions={promotions} />}
    </div>
  );
}
